import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useCreateBeaconAllocation } from "@/lib/api/fund";
import { AllocationForm } from "./AllocationForm";
import { safeParseDecimal } from "@/lib/api/common";
import { useQueryParam } from "@/hooks/use-query-param";

export function CreateAllocationModal() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreating, setIsCreating] = useQueryParam("is_creating_allocation", "");
  const [isSaving, setIsSaving] = useState(false);
  
  const createAllocation = useCreateBeaconAllocation();
  
  const [formData, setFormData] = useState({
    year: new Date().getFullYear(),
    amount_allocated: "",
    is_discretionary: false,
    customer_id: "",
    employee_id: "",
  });

  const isOpen = isCreating === "true";

  const handleClose = () => {
    setIsCreating("");
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      year: new Date().getFullYear(),
      amount_allocated: "",
      is_discretionary: false,
      customer_id: "",
      employee_id: "",
    });
  };

  const handleSave = async () => {
    if (!formData.amount_allocated || (!formData.customer_id && !formData.employee_id)) {
      toast({
        title: "Error",
        description: "Please fill in all required fields and select either a customer or employee.",
        variant: "destructive",
      });
      return;
    }

    const parsedAmount = safeParseDecimal({ key: "amount_allocated" });
    const amount = parsedAmount(formData.amount_allocated);
    if (!amount || parseFloat(amount.toString()) < 0) {
      toast({
        title: "Error",
        description: "Please enter a valid amount.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    
    try {
      const result = await createAllocation({
        year: formData.year,
        amount_allocated: formData.amount_allocated,
        is_discretionary: formData.is_discretionary,
        ...(formData.customer_id ? { customer_id: formData.customer_id } : {}),
        ...(formData.employee_id ? { employee_id: formData.employee_id } : {}),
      });

      if (result.success) {
        toast({
          title: "Success",
          description: "Allocation created successfully.",
        });
        handleClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to create allocation.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Allocation</DialogTitle>
          <DialogDescription>
            Add a new fund allocation for a customer or employee.
          </DialogDescription>
        </DialogHeader>
        
        <AllocationForm 
          formData={formData}
          setFormData={setFormData}
        />
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Creating..." : "Create Allocation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}